import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getSeasons, getMySeasons, getContentSeason } from "../api/seasons";
import { setSeasonRequestContext } from "../api/seasonContext";
import { useAuth } from "./AuthContext";

const fallback = {
  season: null,
  seasons: [],
  participation: null,
  mySeasons: [],
  archiveEdit: false,
  canEdit: true,
  withSeason: (path) => path,
};
const SeasonContext = createContext(fallback);
export const useSeason = () => useContext(SeasonContext);
export function SeasonProvider({ children }) {
  const { user, loading: authLoading, setSeasonRole } = useAuth();
  const userId = user?.user_id;
  const location = useLocation();
  const navigate = useNavigate();
  const requested = new URLSearchParams(location.search).get("season");
  const slug = location.pathname.startsWith("/seasons/")
    ? location.pathname.split("/")[2]
    : null;
  const oldNewsId =
    !requested && location.pathname.match(/^\/news\/(\d+)$/)?.[1];
  const oldCaseId =
    !requested &&
    location.pathname === "/cases" &&
    new URLSearchParams(location.search).get("focus");
  const contentKey = oldNewsId
    ? `news/${oldNewsId}`
    : oldCaseId && /^\d+$/.test(oldCaseId)
      ? `cases/${oldCaseId}`
      : "";
  const [data, setData] = useState({
    seasons: [],
    mine: [],
    ready: false,
    error: "",
  });
  const [revision, setRevision] = useState(0);
  const [lastId, setLastId] = useState(null);
  const [archiveFor, setArchiveFor] = useState(null);
  const refresh = useCallback(() => setRevision((n) => n + 1), []);
  useEffect(() => {
    if (authLoading) return undefined;
    let live = true;
    Promise.all([
      getSeasons(),
      userId ? getMySeasons() : Promise.resolve([]),
      contentKey
        ? getContentSeason(...contentKey.split("/")).catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([seasons, mine, linkedSeason]) => {
        if (live)
          setData({
            seasons,
            mine,
            linkedSeason,
            contentKey,
            ready: true,
            error: "",
            owner: userId,
          });
      })
      .catch((error) => {
        if (live)
          setData((previous) => ({
            ...previous,
            ready: true,
            owner: userId,
            error: error.message,
          }));
      });
    return () => {
      live = false;
    };
  }, [authLoading, userId, revision, contentKey]);
  const season = slug
    ? data.seasons.find((item) => item.slug === slug)
    : requested
      ? data.seasons.find((item) => String(item.id) === requested)
      : (data.contentKey === contentKey && data.linkedSeason) ||
        data.seasons.find((item) => item.id === lastId) ||
        data.seasons.find((item) => item.is_featured) ||
        data.seasons[0];
  const participation =
    data.mine.find((item) => item.season.id === season?.id)?.participation ||
    null;
  const effectiveRole =
    participation && !["withdrawn", "rejected"].includes(participation.status)
      ? participation.role
      : "MEMBER";
  const archiveEdit =
    archiveFor === season?.id && user?.platform_role === "ADMIN";
  // Synchronous capture: child effects can never issue requests for the previous season.
  setSeasonRequestContext(season?.id, archiveEdit);
  useEffect(() => {
    setSeasonRole?.(effectiveRole);
  }, [effectiveRole, setSeasonRole]);
  useEffect(() => {
    if (!season) return;
    setLastId(season.id);
    setArchiveFor(null);
  }, [season]);
  useEffect(() => {
    if (
      !season ||
      (!slug && requested) ||
      (slug && requested === String(season.id))
    )
      return;
    const params = new URLSearchParams(location.search);
    params.set("season", season.id);
    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
        hash: location.hash,
      },
      { replace: true },
    );
  }, [
    requested,
    slug,
    season,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);
  const withSeason = useCallback(
    (path, id = season?.id) => {
      if (!id) return path;
      const url = new URL(path, window.location.origin);
      url.searchParams.set("season", id);
      return `${url.pathname}${url.search}${url.hash}`;
    },
    [season],
  );
  const selectSeason = useCallback(
    (id) => {
      setArchiveFor(null);
      // A detail ID belongs to the old season; switch to the corresponding list.
      const path = location.pathname
        .replace(/^\/news\/[^/]+$/, "/news")
        .replace(/^\/search\/teams\/[^/]+$/, "/team")
        .replace(/^\/seasons\/[^/]+$/, "/seasons");
      const params = new URLSearchParams(location.search);
      params.set("season", id);
      ["offset", "action", "q", "focus"].forEach((key) => params.delete(key));
      navigate({ pathname: path, search: params.toString() });
    },
    [location, navigate],
  );
  const staff =
    user?.platform_role === "ADMIN" || effectiveRole === "TRACK_COORDINATOR";
  const canEdit =
    season?.status === "completed"
      ? archiveEdit
      : staff ||
        (["active", "recruiting"].includes(season?.status) &&
          (participation?.status === "accepted" ||
            ["MENTOR", "EDE"].includes(effectiveRole)));
  const value = useMemo(
    () => ({
      season,
      seasons: data.seasons,
      mySeasons: data.mine,
      participation,
      archiveEdit,
      setArchiveEdit: (value) => setArchiveFor(value ? season.id : null),
      canEdit,
      withSeason,
      selectSeason,
      refresh,
    }),
    [
      season,
      data,
      participation,
      archiveEdit,
      canEdit,
      withSeason,
      selectSeason,
      refresh,
    ],
  );
  if (
    authLoading ||
    !data.ready ||
    data.owner !== userId ||
    (user && user.platform_role !== "ADMIN" && user.role !== effectiveRole)
  )
    return (
      <div className="season-loading" role="status">
        Загружаем сезоны…
      </div>
    );
  if (data.error)
    return (
      <div className="season-loading" role="alert">
        <p>Не удалось загрузить сезоны. {data.error}</p>
        <button onClick={refresh}>Повторить</button>
      </div>
    );
  if (!season)
    return (
      <div className="season-loading">
        <h1>Сезон недоступен</h1>
        <p>Проверьте ссылку или выберите опубликованный сезон.</p>
        <button onClick={() => navigate("/")}>На главную</button>
      </div>
    );
  return (
    <SeasonContext.Provider value={value}>
      <div key={season.id} style={{ display: "contents" }}>
        {children}
      </div>
    </SeasonContext.Provider>
  );
}
